import {partition} from 'lodash';

const hoursPointsColumnNames = [
	'billable-time-entries',
	'non-billable-time-entries',
	'over-forecast',
	'forecast',
	'remaining',
	'time-entries',
];

type AvailableColumn = {
	name: string;
	disabled: boolean;
	checked: boolean;
	translationId: string;
	nestedOptions?: AvailableColumn[];
};
export const groupEyeOptions = (availableColumns: AvailableColumn[]): AvailableColumn[] => {
	const [hoursPointsOptions, restColumns] = partition(availableColumns, column => {
		return hoursPointsColumnNames.includes(column.name);
	});
	const hoursPointsOption = {
		name: 'hours-points',
		disabled: false,
		checked: hoursPointsOptions.some(option => option.checked),
		translationId: 'common.hours_and_points',
		nestedOptions: hoursPointsOptions,
	};
	return [hoursPointsOption, ...restColumns];
};

const getColumnNameWithEyeOptionPrefix = (column: AvailableColumn): string => {
	if (hoursPointsColumnNames.includes(column.name)) {
		return `hours-points.${column.name}`;
	}

	return column.name;
};

type EyeOption = {
	[key: string]: boolean;
};
export const eyeOptionsToCols = (eyeOptions: EyeOption, availableColumns: AvailableColumn[]): AvailableColumn[] => {
	return availableColumns.map(column => {
		const path = getColumnNameWithEyeOptionPrefix(column);
		const checkedFromEye = eyeOptions[path];
		return {
			...column,
			checked: checkedFromEye !== undefined ? checkedFromEye : column.checked,
		};
	});
};

export type StoredColumnSelection = {
	id: string;
	checked: boolean;
};

type SelectableColumn = {
	name: string;
	checked?: boolean;
	sprintDefault?: boolean;
	hide?: boolean;
};

/**
 * Resolve each column's visibility from a stored selection.
 *
 * A column with no stored entry falls back to its `sprintDefault`, so the set of columns
 * a brand new browser starts with is the curated sprint default rather than everything.
 * Columns flagged `hide` are never offered in the eye and keep whatever they were given.
 *
 * Returns new column objects; the input is left untouched.
 */
export const applyStoredColumnSelection = <T extends SelectableColumn>(
	columns: T[],
	storedColumns: StoredColumnSelection[] | null | undefined
): T[] => {
	return columns.map(column => {
		if (column.hide) {
			return {...column};
		}
		const storedColumn = storedColumns?.find(stored => stored.id === column.name);
		return {
			...column,
			checked: (storedColumn ? storedColumn.checked : column.sprintDefault) || false,
		};
	});
};

/**
 * Build the selection to persist for the columns currently on screen.
 *
 * The stored selection is shared by every project, but `columns` only covers the columns
 * the open project actually offers — which varies with its budget type, progress tracking
 * and sprint settings. Entries for columns this project does not offer are carried over
 * unchanged, so saving here does not wipe what the user chose on a different project.
 */
export const mergeSelectedColumns = (
	storedColumns: StoredColumnSelection[] | null | undefined,
	columns: SelectableColumn[]
): StoredColumnSelection[] => {
	const currentSelection = columns.map(column => ({id: column.name, checked: !!column.checked}));
	const currentNames = new Set(currentSelection.map(column => column.id));
	const carriedOver = (storedColumns ?? []).filter(stored => !currentNames.has(stored.id));

	return [...currentSelection, ...carriedOver];
};
